import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl, currentSchool } from '../config';
import * as XLSX from 'xlsx';

// Clubs Attendance Report Component
const ClubsAttendanceReportInline = () => {
  const [clubsData, setClubsData] = useState([]);
  const [allPeriodsData, setAllPeriodsData] = useState([]);
  const [clubsAttendanceHistory, setClubsAttendanceHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState("all-clubs");
  const [selectedClub, setSelectedClub] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const clubsResponse = await axios.get(buildApiUrl('/api/admin/clubs/all'));
      // Store all periods
      const periods = clubsResponse.data.periods || [];
      setAllPeriodsData(periods);
      
      // Filter clubs based on selected period
      const clubs = selectedPeriod 
        ? (periods.find(p => p.period?.id === selectedPeriod || p.id === selectedPeriod)?.clubs || [])
        : periods.flatMap(p => p.clubs || []);
      setClubsData(clubs);

      const attendanceByClub = {};
      for (const club of clubs) {
        try {
          const attendanceResponse = await axios.get(buildApiUrl(`/api/clubs/${club.id}/attendance`));
          if (attendanceResponse.data.success) {
            attendanceByClub[club.id] = attendanceResponse.data.attendance_records || [];
          }
        } catch (error) {
          console.error(`Failed to load attendance for club ${club.id}:`, error);
          attendanceByClub[club.id] = [];
        }
      }
      setClubsAttendanceHistory(attendanceByClub);
    } catch (error) {
      console.error('Error loading clubs attendance:', error);
      alert('Failed to load clubs attendance data');
    } finally {
      setIsLoading(false);
    }
  };

  const exportAttendanceByPeriod = () => {
    if (!selectedPeriod || clubsData.length === 0) {
      alert('Please select a period with clubs to export');
      return;
    }

    try {
      const periodName = allPeriodsData.find(p => (p.period?.id || p.id) === selectedPeriod)?.period?.name || 
                         allPeriodsData.find(p => (p.period?.id || p.id) === selectedPeriod)?.name || 
                         'Unknown Period';
      
      // Create a new workbook
      const wb = XLSX.utils.book_new();
      
      // Create a sheet for each club
      clubsData.forEach((club) => {
        const records = clubsAttendanceHistory[club.id] || [];
        
        // Create sheet data array
        const sheetData = [];
        
        // Add club header info
        sheetData.push(['Club:', club.name]);
        sheetData.push(['Teacher:', club.teacher_name]);
        sheetData.push(['Day:', club.day_of_week]);
        sheetData.push(['Time:', `${club.start_time} - ${club.end_time}`]);
        sheetData.push([]); // Empty row
        
        if (records.length === 0) {
          sheetData.push(['No attendance records']);
        } else {
          // Group by session
          const sessionMap = {};
          records.forEach(record => {
            if (!sessionMap[record.session_date]) {
              sessionMap[record.session_date] = [];
            }
            sessionMap[record.session_date].push(record);
          });
          
          // Add headers
          sheetData.push(['Session Date', 'Student ID', 'Student Name', 'Status', 'Recorded At']);
          
          // Add data rows
          Object.keys(sessionMap).sort().forEach(sessionDate => {
            sessionMap[sessionDate].forEach(record => {
              sheetData.push([
                sessionDate,
                record.student_school_id,
                record.student_name || 'N/A',
                record.status,
                new Date(record.recorded_at).toLocaleString()
              ]);
            });
          });
        }
        
        // Create worksheet
        const ws = XLSX.utils.aoa_to_sheet(sheetData);
        
        // Set column widths
        ws['!cols'] = [
          { wch: 15 }, // Session Date
          { wch: 15 }, // Student ID
          { wch: 25 }, // Student Name
          { wch: 10 }, // Status
          { wch: 20 }  // Recorded At
        ];
        
        // Clean sheet name (Excel has a 31 char limit and doesn't allow certain chars)
        let sheetName = club.name.substring(0, 31).replace(/[:\\/?*\[\]]/g, '');
        
        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });
      
      // Generate Excel file and trigger download
      XLSX.writeFile(wb, `Attendance_${periodName.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      alert('Attendance data exported successfully!');
    } catch (error) {
      console.error('Error exporting attendance:', error);
      alert('Failed to export attendance data');
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedPeriod]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">Club Attendance Report</h2>
        <div className="flex gap-3">
          <select
            value={selectedPeriod || ''}
            onChange={(e) => setSelectedPeriod(e.target.value ? parseInt(e.target.value) : null)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value="">All Periods</option>
            {allPeriodsData.map(period => (
              <option key={period.period?.id || period.id} value={period.period?.id || period.id}>
                {period.period?.name || period.name}
              </option>
            ))}
          </select>
          <button 
            onClick={loadData}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            disabled={isLoading}
          >
            {isLoading ? '⏳ Loading...' : '🔄 Refresh'}
          </button>
        </div>
      </div>

      <div className="flex border-b mb-6 gap-2">
        <button
          className={`px-4 py-2 font-medium ${activeSubTab === "all-clubs" ? "border-b-2 border-purple-600 text-purple-600" : "text-gray-600"}`}
          onClick={() => setActiveSubTab("all-clubs")}
        >
          📊 All Clubs
        </button>
        <button
          className={`px-4 py-2 font-medium ${activeSubTab === "by-club" ? "border-b-2 border-purple-600 text-purple-600" : "text-gray-600"}`}
          onClick={() => setActiveSubTab("by-club")}
        >
          📈 By Club
        </button>
        <button
          className={`px-4 py-2 font-medium ${activeSubTab === "by-period" ? "border-b-2 border-purple-600 text-purple-600" : "text-gray-600"}`}
          onClick={() => setActiveSubTab("by-period")}
        >
          📅 By Period
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading attendance data...</p>
        </div>
      ) : clubsData.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600">No clubs found</p>
        </div>
      ) : (
        <>
          {activeSubTab === "all-clubs" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <div className="text-3xl font-bold text-blue-600">{clubsData.length}</div>
                  <div className="text-sm text-blue-700">Total Clubs</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <div className="text-3xl font-bold text-green-600">
                    {Object.values(clubsAttendanceHistory).reduce((sum, records) => sum + records.length, 0)}
                  </div>
                  <div className="text-sm text-green-700">Attendance Records</div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                  <div className="text-3xl font-bold text-purple-600">
                    {Object.values(clubsAttendanceHistory).reduce((sum, records) => sum + new Set(records.map(r => r.session_date)).size, 0)}
                  </div>
                  <div className="text-sm text-purple-700">Total Sessions</div>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                  <div className="text-3xl font-bold text-orange-600">
                    {Object.values(clubsAttendanceHistory).reduce((sum, records) => sum + new Set(records.map(r => r.teacher_email)).size, 0)}
                  </div>
                  <div className="text-sm text-orange-700">Teachers</div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-900">Clubs Summary</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Club Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Teacher</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Sessions</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Records</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Last Recording</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {clubsData.map((club) => {
                        const clubRecords = clubsAttendanceHistory[club.id] || [];
                        const sessionCount = new Set(clubRecords.map(r => r.session_date)).size;
                        const lastRecord = clubRecords.length > 0 ? clubRecords[0] : null;
                        return (
                          <tr key={club.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => { setSelectedClub(club.id); setActiveSubTab("by-club"); }}>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">{club.name}</td>
                            <td className="px-6 py-4 text-sm text-gray-600">{club.teacher_name}</td>
                            <td className="px-6 py-4 text-sm text-gray-600">{sessionCount}</td>
                            <td className="px-6 py-4 text-sm text-gray-600">{clubRecords.length}</td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {lastRecord ? new Date(lastRecord.recorded_at).toLocaleDateString('en-GB') : 'N/A'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeSubTab === "by-club" && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Select Club</label>
                <select
                  value={selectedClub || ""}
                  onChange={(e) => setSelectedClub(parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Choose a club...</option>
                  {clubsData.map((club) => (
                    <option key={club.id} value={club.id}>{club.name} ({club.teacher_name || 'No Teacher'})</option>
                  ))}
                </select>
              </div>

              {selectedClub && clubsAttendanceHistory[selectedClub] ? (
                <div className="space-y-4">
                  {(() => {
                    const club = clubsData.find(c => c.id === selectedClub);
                    const records = clubsAttendanceHistory[selectedClub];
                    const sessionCount = new Set(records.map(r => r.session_date)).size;
                    const totalStudents = new Set(records.map(r => r.student_school_id)).size;
                    const presentCount = records.filter(r => r.status === 'present').length;
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="text-2xl font-bold text-blue-600">{club?.name}</div>
                          <div className="text-sm text-blue-700">{club?.teacher_name || 'No Teacher'}</div>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="text-2xl font-bold text-green-600">{sessionCount}</div>
                          <div className="text-sm text-green-700">Sessions</div>
                        </div>
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                          <div className="text-2xl font-bold text-purple-600">{totalStudents}</div>
                          <div className="text-sm text-purple-700">Unique Students</div>
                        </div>
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                          <div className="text-2xl font-bold text-orange-600">{presentCount}</div>
                          <div className="text-sm text-orange-700">Present Records</div>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                      <h3 className="text-lg font-semibold text-gray-900">Sessions</h3>
                    </div>
                    <div className="divide-y divide-gray-200">
                      {(() => {
                        const sessions = clubsAttendanceHistory[selectedClub] || [];
                        return sessions.length === 0 ? (
                          <div className="px-6 py-8 text-center text-gray-600">
                            <p>No attendance records for this club yet</p>
                          </div>
                        ) : sessions.map((session) => (
                            <div key={session.session_date} className="px-6 py-4">
                              <div className="flex justify-between items-center mb-3">
                                <div>
                                  <h4 className="text-md font-semibold text-gray-900">
                                    {new Date(session.session_date).toLocaleDateString('en-GB')}
                                  </h4>
                                  <p className="text-sm text-gray-600">
                                    Recorded: {new Date(new Date(session.recorded_at).getTime() + 3 * 60 * 60 * 1000).toLocaleDateString('en-GB')} at {new Date(new Date(session.recorded_at).getTime() + 3 * 60 * 60 * 1000).toLocaleTimeString('en-GB')}
                                  </p>
                                </div>
                                <div className="inline-flex items-center gap-4 px-4 py-2 bg-gray-50 rounded-lg">
                                  <span className="text-sm font-medium text-green-600">✓ {session.present_count}</span>
                                  <span className="text-sm font-medium text-red-600">✗ {session.absent_count}</span>
                                </div>
                              </div>
                              <div className="mt-3 space-y-2">
                                {session.student_records.map((record, idx) => (
                                  <div key={idx} className={`flex justify-between items-center p-2 rounded text-sm ${record.status === 'present' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                                    <div>
                                      <span className="font-medium text-gray-900">{record.student_name || record.student_school_id}</span>
                                      {record.student_name && <span className="text-gray-600 text-xs ml-2">({record.student_school_id})</span>}
                                    </div>
                                    <span className={`font-semibold ${record.status === 'present' ? 'text-green-600' : 'text-red-600'}`}>
                                      {record.status === 'present' ? '✓' : '✗'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ));
                      })()}
                    </div>
                  </div>
                </div>
              ) : selectedClub && (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-600">No attendance records for this club yet</p>
                </div>
              )}
            </div>
          )}

          {activeSubTab === "by-period" && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Export Attendance by Period</h3>
                  <button
                    onClick={() => exportAttendanceByPeriod()}
                    disabled={clubsData.length === 0}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <span>📥</span>
                    <span>Export to Excel</span>
                  </button>
                </div>
                
                {selectedPeriod ? (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-blue-900 font-medium">
                        Selected Period: {allPeriodsData.find(p => (p.period?.id || p.id) === selectedPeriod)?.period?.name || allPeriodsData.find(p => (p.period?.id || p.id) === selectedPeriod)?.name}
                      </p>
                      <p className="text-blue-700 text-sm mt-1">{clubsData.length} clubs in this period</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {clubsData.map((club) => {
                        const records = clubsAttendanceHistory[club.id] || [];
                        const sessionCount = new Set(records.map(r => r.session_date)).size;
                        return (
                          <div key={club.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <h4 className="font-semibold text-gray-900">{club.name}</h4>
                            <p className="text-sm text-gray-600 mt-1">{club.teacher_name}</p>
                            <div className="mt-3 space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Sessions:</span>
                                <span className="font-medium">{sessionCount}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Records:</span>
                                <span className="font-medium">{records.length}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-600">Select a period from the dropdown above to view and export attendance data</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ClubBookingManager = () => {
  const [activeTab, setActiveTab] = useState('periods');
  const [bookingPeriods, setBookingPeriods] = useState([]);
  const [clubs, setClubs] = useState({});
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  
  // Bookings state
  const [allBookings, setAllBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [selectedBookingPeriod, setSelectedBookingPeriod] = useState(null);
  const [bookingStats, setBookingStats] = useState({});
  
  // Students and hierarchical selection
  const [allStudents, setAllStudents] = useState([]);
  const [expandedYears, setExpandedYears] = useState([]);
  const [expandedForms, setExpandedForms] = useState([]);
  
  // Form states
  const [showPeriodForm, setShowPeriodForm] = useState(false);
  const [showClubForm, setShowClubForm] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [editingClub, setEditingClub] = useState(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [periodForm, setPeriodForm] = useState({
    name: '',
    description: '',
    booking_opens_at: '',
    booking_closes_at: '',
    clubs_start_date: '',
    clubs_end_date: ''
  });
  const [clubForm, setClubForm] = useState({
    name: '',
    description: '',
    day_of_week: 'Monday',
    start_time: '15:30',
    end_time: '16:30',
    capacity: 20,
    teacher_email: '',
    location: '',
    age_groups: [],
    selected_students: [],
    requirements: ''
  });

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const yearGroups = ['Pre-School', 'Reception', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6'];

  useEffect(() => {
    loadData();
    loadStudents();
  }, []);

  useEffect(() => {
    if (activeTab === 'bookings') {
      loadBookingsData();
    }
  }, [activeTab, selectedBookingPeriod, clubs]);

  useEffect(() => {
    // Update filtered bookings when period changes
    if (selectedBookingPeriod && allBookings.length > 0) {
      const periodClubIds = clubs[selectedBookingPeriod.id]?.map(club => club.id) || [];
      const filtered = allBookings.filter(booking => 
        periodClubIds.includes(booking.club_id)
      );
      setFilteredBookings(filtered);
    } else {
      setFilteredBookings(allBookings);
    }
  }, [selectedBookingPeriod, allBookings, clubs]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load booking periods
      const periodsRes = await axios.get(buildApiUrl('/api/club-booking-periods'));
      if (periodsRes.data.success) {
        setBookingPeriods(periodsRes.data.periods);
      }

      // Load staff
      const staffFile = currentSchool === 'demo' ? 'staff-demo.json' : 'staff.json';
      console.log(`👥 Loading staff from: ${staffFile}`);
      const staffRes = await axios.get(`/${staffFile}`);
      setStaff(staffRes.data);

      // Load clubs for all periods
      if (periodsRes.data.success) {
        const clubsData = {};
        for (const period of periodsRes.data.periods) {
          try {
            const clubsRes = await axios.get(buildApiUrl(`/api/club-booking-periods/${period.id}/clubs`));
            if (clubsRes.data.success) {
              clubsData[period.id] = clubsRes.data.clubs;
            }
          } catch (error) {
            console.error(`Error loading clubs for period ${period.id}:`, error);
            clubsData[period.id] = [];
          }
        }
        setClubs(clubsData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async () => {
    try {
      const response = await axios.get(buildApiUrl('/api/consent-forms/students-years'));
      if (response.data.success) {
        setAllStudents(response.data.students || []);
      }
    } catch (error) {
      console.error('Error loading students:', error);
    }
  };

  const loadBookingsData = async () => {
    setLoading(true);
    try {
      // Load all bookings
      const bookingsRes = await axios.get(buildApiUrl('/api/club-bookings'));
      if (bookingsRes.data.success) {
        setAllBookings(bookingsRes.data.bookings);
        
        // Calculate statistics
        const stats = {};
        const confirmedBookings = bookingsRes.data.bookings.filter(b => b.booking_status === 'confirmed');
        
        // Group by club
        const clubStats = {};
        confirmedBookings.forEach(booking => {
          if (!clubStats[booking.club_id]) {
            clubStats[booking.club_id] = {
              club_name: booking.club_name,
              day_of_week: booking.day_of_week,
              start_time: booking.start_time,
              end_time: booking.end_time,
              confirmed_count: 0,
              total_count: 0
            };
          }
          clubStats[booking.club_id].confirmed_count++;
        });
        
        // Add total counts (including waitlist, etc.)
        bookingsRes.data.bookings.forEach(booking => {
          if (clubStats[booking.club_id]) {
            clubStats[booking.club_id].total_count++;
          }
        });
        
        setBookingStats(clubStats);
        
        // Filter bookings based on selected period
        if (selectedBookingPeriod) {
          const periodClubIds = clubs[selectedBookingPeriod.id]?.map(club => club.id) || [];
          const filtered = bookingsRes.data.bookings.filter(booking => 
            periodClubIds.includes(booking.club_id)
          );
          setFilteredBookings(filtered);
        } else {
          setFilteredBookings(bookingsRes.data.bookings);
        }
      }
    } catch (error) {
      console.error('Error loading bookings data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Hierarchical selection helper functions
  const getYearGroups = () => {
    const years = ['Pre-School', 'Reception', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 
                   'Year 6', 'Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11', 'Year 12', 'Year 13'];
    const filtered = years.filter(year => allStudents.some(s => s.year_group === year));
    return filtered;
  };

  const getFormsInYear = (year) => {
    const forms = [...new Set(allStudents.filter(s => s.year_group === year).map(s => s.form))];
    return forms.filter(f => f).sort();
  };

  const getStudentsInForm = (year, form) => {
    return allStudents.filter(s => s.year_group === year && s.form === form).sort((a, b) => 
      a.full_name.localeCompare(b.full_name)
    );
  };

  const getFormsFromSelectedStudents = (selectedStudentIds) => {
    if (!selectedStudentIds || selectedStudentIds.length === 0) return [];
    const formsSet = new Set();
    selectedStudentIds.forEach(studentId => {
      const student = allStudents.find(s => s.school_id === studentId);
      if (student && student.form) {
        formsSet.add(student.form);
      }
    });
    return Array.from(formsSet).sort();
  };

  const toggleYear = (year) => {
    setExpandedYears(prev => 
      prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]
    );
  };

  const toggleForm = (formKey) => {
    setExpandedForms(prev => 
      prev.includes(formKey) ? prev.filter(f => f !== formKey) : [...prev, formKey]
    );
  };

  const isStudentSelected = (studentId) => {
    return clubForm.selected_students.includes(studentId);
  };

  const toggleStudent = (studentId) => {
    setClubForm(prev => ({
      ...prev,
      selected_students: prev.selected_students.includes(studentId)
        ? prev.selected_students.filter(id => id !== studentId)
        : [...prev.selected_students, studentId]
    }));
  };

  const selectAllInForm = (year, form) => {
    const studentsInForm = allStudents.filter(s => s.year_group === year && s.form === form);
    const studentIds = studentsInForm.map(s => s.school_id);
    const allSelected = studentIds.every(id => clubForm.selected_students.includes(id));
    
    if (allSelected) {
      // Deselect all
      setClubForm(prev => ({
        ...prev,
        selected_students: prev.selected_students.filter(id => !studentIds.includes(id))
      }));
    } else {
      // Select all
      setClubForm(prev => ({
        ...prev,
        selected_students: [...new Set([...prev.selected_students, ...studentIds])]
      }));
    }
  };

  const createBookingPeriod = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(buildApiUrl('/api/club-booking-periods'), periodForm);
      if (response.data.success) {
        alert('Booking period created successfully!');
        setShowPeriodForm(false);
        setPeriodForm({
          name: '',
          description: '',
          booking_opens_at: '',
          booking_closes_at: '',
          clubs_start_date: '',
          clubs_end_date: ''
        });
        loadData();
      } else {
        alert('Error creating booking period: ' + response.data.error);
      }
    } catch (error) {
      console.error('Error creating booking period:', error);
      alert('Error creating booking period');
    } finally {
      setLoading(false);
    }
  };

  const createClub = async (e) => {
    e.preventDefault();
    if (!selectedPeriod) {
      alert('Please select a booking period first');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(buildApiUrl(`/api/club-booking-periods/${selectedPeriod.id}/clubs`), clubForm);
      if (response.data.success) {
        alert('Club created successfully!');
        setShowClubForm(false);
        setClubForm({
          name: '',
          description: '',
          day_of_week: 'Monday',
          start_time: '15:30',
          end_time: '16:30',
          capacity: 20,
          teacher_email: '',
          location: '',
          age_groups: [],
          selected_students: [],
          requirements: ''
        });
        loadData();
      } else {
        alert('Error creating club: ' + response.data.error);
      }
    } catch (error) {
      console.error('Error creating club:', error);
      alert('Error creating club');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!uploadFile) {
      alert('Please select a file to upload');
      return;
    }
    
    if (!selectedPeriod) {
      alert('Please select a booking period first');
      return;
    }

    setLoading(true);
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      
      const response = await axios.post(
        buildApiUrl(`/api/club-booking-periods/${selectedPeriod.id}/clubs/bulk-upload`),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        setUploadResult(response.data);
        alert(`✅ Successfully created ${response.data.total_created} clubs!${response.data.errors?.length ? `\n\n⚠️ ${response.data.total_errors} errors occurred. Check the results for details.` : ''}`);
        setUploadFile(null);
        loadData();
      } else {
        alert('Error uploading clubs: ' + response.data.error);
      }
    } catch (error) {
      console.error('Error uploading clubs:', error);
      alert('Error uploading clubs: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    // Create a link to download the template
    const link = document.createElement('a');
    link.href = '/clubs_bulk_upload_template.csv';
    link.download = 'clubs_bulk_upload_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Edit and delete functions for periods
  const editPeriod = (period) => {
    setEditingPeriod(period);
    setPeriodForm({
      name: period.name,
      description: period.description || '',
      booking_opens_at: new Date(period.booking_opens_at).toISOString().slice(0, 16),
      booking_closes_at: new Date(period.booking_closes_at).toISOString().slice(0, 16),
      clubs_start_date: new Date(period.clubs_start_date).toISOString().slice(0, 16),
      clubs_end_date: new Date(period.clubs_end_date).toISOString().slice(0, 16)
    });
    setShowPeriodForm(true);
  };

  const updatePeriod = async (e) => {
    e.preventDefault();
    if (!editingPeriod) return;
    
    setLoading(true);
    try {
      const response = await axios.put(buildApiUrl(`/api/club-booking-periods/${editingPeriod.id}`), periodForm);
      if (response.data.success) {
        alert('Period updated successfully!');
        setShowPeriodForm(false);
        setEditingPeriod(null);
        setPeriodForm({
          name: '',
          description: '',
          booking_opens_at: '',
          booking_closes_at: '',
          clubs_start_date: '',
          clubs_end_date: ''
        });
        loadData();
      } else {
        alert('Error updating period: ' + response.data.error);
      }
    } catch (error) {
      console.error('Error updating period:', error);
      alert('Error updating period');
    } finally {
      setLoading(false);
    }
  };

  const deletePeriod = async (period, force = false) => {
    // First confirmation - basic delete warning
    if (!force && !confirm(`Are you sure you want to delete "${period.name}"? This will also delete all associated clubs.`)) {
      return;
    }
    
    setLoading(true);
    try {
      const url = force 
        ? buildApiUrl(`/api/club-booking-periods/${period.id}?force=true`)
        : buildApiUrl(`/api/club-booking-periods/${period.id}`);
        
      const response = await axios.delete(url);
      
      if (response.data.success) {
        if (response.data.warning) {
          alert('⚠️ ' + response.data.message + '\n\n' + response.data.warning);
        } else {
          alert('✅ ' + response.data.message);
        }
        loadData();
      } else if (response.data.warning) {
        // Show detailed warning and ask for force confirmation
        const warningMessage = response.data.message + '\n\nBooking Details:\n' + 
          response.data.details.join('\n') + 
          '\n\n⚠️ This action cannot be undone and will affect parents who have booked clubs.';
          
        if (confirm(warningMessage + '\n\nDo you want to proceed with deletion?')) {
          // Recursive call with force=true
          deletePeriod(period, true);
          return;
        }
      } else {
        alert('❌ Error deleting period: ' + response.data.error);
      }
    } catch (error) {
      console.error('Error deleting period:', error);
      if (error.response?.data?.warning) {
        // Handle warning from server error response
        const data = error.response.data;
        const warningMessage = data.message + '\n\nBooking Details:\n' + 
          data.details.join('\n') + 
          '\n\n⚠️ This action cannot be undone and will affect parents who have booked clubs.';
          
        if (confirm(warningMessage + '\n\nDo you want to proceed with deletion?')) {
          deletePeriod(period, true);
          return;
        }
      } else {
        alert('❌ Error deleting period');
      }
    } finally {
      setLoading(false);
    }
  };

  // Edit and delete functions for clubs
  const editClub = (club) => {
    setEditingClub(club);
    setClubForm({
      name: club.name,
      description: club.description || '',
      day_of_week: club.day_of_week,
      start_time: club.start_time,
      end_time: club.end_time,
      capacity: club.capacity,
      teacher_email: club.teacher_email,
      location: club.location || '',
      age_groups: club.age_groups || [],
      selected_students: club.selected_students || [],
      requirements: club.requirements || ''
    });
    setShowClubForm(true);
  };

  const updateClub = async (e) => {
    e.preventDefault();
    if (!editingClub) return;
    
    setLoading(true);
    try {
      const response = await axios.put(buildApiUrl(`/api/clubs/${editingClub.id}`), clubForm);
      if (response.data.success) {
        alert('Club updated successfully!');
        setShowClubForm(false);
        setEditingClub(null);
        setClubForm({
          name: '',
          description: '',
          day_of_week: 'Monday',
          start_time: '15:30',
          end_time: '16:30',
          capacity: 20,
          teacher_email: '',
          location: '',
          age_groups: [],
          selected_students: [],
          requirements: ''
        });
        loadData();
      } else {
        alert('Error updating club: ' + response.data.error);
      }
    } catch (error) {
      console.error('Error updating club:', error);
      alert('Error updating club');
    } finally {
      setLoading(false);
    }
  };

  const deleteClub = async (club) => {
    if (!confirm(`Are you sure you want to delete "${club.name}"? This cannot be undone.`)) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.delete(buildApiUrl(`/api/clubs/${club.id}`));
      if (response.data.success) {
        alert('Club deleted successfully!');
        loadData();
      } else {
        alert('Error deleting club: ' + response.data.error);
      }
    } catch (error) {
      console.error('Error deleting club:', error);
      alert('Error deleting club');
    } finally {
      setLoading(false);
    }
  };

  const updatePeriodStatus = async (periodId, newStatus) => {
    try {
      const response = await axios.put(buildApiUrl(`/api/club-booking-periods/${periodId}/status?status=${newStatus}`));
      if (response.data.success) {
        alert(`Period status updated to ${newStatus}`);
        loadData();
      } else {
        alert('Error updating period status: ' + response.data.error);
      }
    } catch (error) {
      console.error('Error updating period status:', error);
      alert('Error updating period status');
    }
  };

  const togglePublishPeriod = async (period) => {
    const action = period.published_to_parents ? 'unpublish' : 'publish';
    if (!window.confirm(`${action === 'publish' ? 'Publish' : 'Unpublish'} "${period.name}" ${action === 'publish' ? 'so parents can see and book clubs?' : 'to hide it from parents?'}`)) return;
    try {
      const response = await axios.put(buildApiUrl(`/api/club-booking-periods/${period.id}/publish`));
      if (response.data.success) {
        loadData();
      } else {
        alert('Error: ' + response.data.error);
      }
    } catch (error) {
      console.error('Error toggling publish:', error);
      alert('Error updating publish status');
    }
  };

  const formatDateTime = (datetime) => {
    return new Date(datetime).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'closed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading && bookingPeriods.length === 0) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading club booking data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen max-w-full overflow-x-auto">
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">🏃‍♂️</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Club Booking Manager</h1>
          </div>
          <p className="text-gray-600">Manage after school clubs and student bookings</p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6 overflow-x-auto">
          <nav className="flex space-x-8 min-w-max">
            <button
              onClick={() => setActiveTab('periods')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'periods'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Booking Periods
            </button>
            <button
              onClick={() => setActiveTab('clubs')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'clubs'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Manage Clubs
            </button>
            <button
              onClick={() => setActiveTab('bookings')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'bookings'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              View Bookings
            </button>
            <button
              onClick={() => setActiveTab('attendance')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'attendance'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              📋 Attendance
            </button>
          </nav>
        </div>

      {/* Booking Periods Tab */}
      {activeTab === 'periods' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h2 className="text-xl font-semibold text-gray-900">Booking Periods</h2>
            <button
              onClick={() => {
                setShowPeriodForm(!showPeriodForm);
                if (showPeriodForm) {
                  setEditingPeriod(null);
                  setPeriodForm({
                    name: '',
                    description: '',
                    booking_opens_at: '',
                    booking_closes_at: '',
                    clubs_start_date: '',
                    clubs_end_date: ''
                  });
                }
              }}
              className="w-full sm:w-auto px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              {showPeriodForm ? 'Cancel' : 'Create New Period'}
            </button>
          </div>

          {/* Create Period Form */}
          {showPeriodForm && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingPeriod ? 'Edit Booking Period' : 'Create New Booking Period'}
              </h3>
              <form onSubmit={editingPeriod ? updatePeriod : createBookingPeriod} className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      required
                      value={periodForm.name}
                      onChange={(e) => setPeriodForm({...periodForm, name: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="e.g., After School Clubs - Term 1"
                    />
                  </div>
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      rows="2"
                      value={periodForm.description}
                      onChange={(e) => setPeriodForm({...periodForm, description: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Optional description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Booking Opens</label>
                    <input
                      type="datetime-local"
                      required
                      value={periodForm.booking_opens_at}
                      onChange={(e) => setPeriodForm({...periodForm, booking_opens_at: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Booking Closes</label>
                    <input
                      type="datetime-local"
                      required
                      value={periodForm.booking_closes_at}
                      onChange={(e) => setPeriodForm({...periodForm, booking_closes_at: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Clubs Start Date</label>
                    <input
                      type="datetime-local"
                      required
                      value={periodForm.clubs_start_date}
                      onChange={(e) => setPeriodForm({...periodForm, clubs_start_date: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Clubs End Date</label>
                    <input
                      type="datetime-local"
                      required
                      value={periodForm.clubs_end_date}
                      onChange={(e) => setPeriodForm({...periodForm, clubs_end_date: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPeriodForm(false);
                      setEditingPeriod(null);
                      setPeriodForm({
                        name: '',
                        description: '',
                        booking_opens_at: '',
                        booking_closes_at: '',
                        clubs_start_date: '',
                        clubs_end_date: ''
                      });
                    }}
                    className="w-full sm:w-auto px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full sm:w-auto px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    {loading ? (editingPeriod ? 'Updating...' : 'Creating...') : (editingPeriod ? 'Update Period' : 'Create Period')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Periods List */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {bookingPeriods.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📅</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Booking Periods</h3>
                <p className="text-gray-600">Create your first booking period to get started</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        Booking Period
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        Club Period
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                        Clubs
                      </th>
                      <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {bookingPeriods.map((period) => (
                      <tr key={period.id} className="hover:bg-gray-50">
                        <td className="px-3 sm:px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900 break-words">{period.name}</div>
                            {period.description && (
                              <div className="text-sm text-gray-500 break-words">{period.description}</div>
                            )}
                            {/* Show booking info on mobile */}
                            <div className="md:hidden mt-2 space-y-1">
                              <div className="text-xs text-gray-600">
                                Opens: {new Date(period.booking_opens_at).toLocaleDateString()}
                              </div>
                              <div className="text-xs text-gray-600">
                                Closes: {new Date(period.booking_closes_at).toLocaleDateString()}
                              </div>
                              <div className="text-xs text-gray-600">
                                Duration: {new Date(period.clubs_start_date).toLocaleDateString()} - {new Date(period.clubs_end_date).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 text-sm text-gray-900 hidden md:table-cell">
                          <div className="space-y-1">
                            <div className="text-xs">Opens: {new Date(period.booking_opens_at).toLocaleDateString()}</div>
                            <div className="text-xs">Closes: {new Date(period.booking_closes_at).toLocaleDateString()}</div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 text-sm text-gray-900 hidden md:table-cell">
                          <div className="space-y-1">
                            <div className="text-xs">Starts: {new Date(period.clubs_start_date).toLocaleDateString()}</div>
                            <div className="text-xs">Ends: {new Date(period.clubs_end_date).toLocaleDateString()}</div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4">
                          <div className="space-y-1">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(period.is_active)}`}>
                              {period.is_active}
                            </span>
                            <div>
                              {period.published_to_parents
                                ? <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">👁 Published</span>
                                : <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">🔒 Hidden</span>
                              }
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 text-sm text-gray-900 hidden sm:table-cell">
                          {clubs[period.id]?.length || 0} clubs
                        </td>
                        <td className="px-3 sm:px-6 py-4 text-sm font-medium">
                          <div className="flex flex-col space-y-2">
                            <div className="flex flex-wrap gap-1">
                              <button
                                onClick={() => setSelectedPeriod(period)}
                                className="text-purple-600 hover:text-purple-900 text-xs px-2 py-1 rounded bg-purple-50 hover:bg-purple-100"
                              >
                                Manage
                              </button>
                              <button
                                onClick={() => editPeriod(period)}
                                className="text-blue-600 hover:text-blue-900 text-xs px-2 py-1 rounded bg-blue-50 hover:bg-blue-100"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => deletePeriod(period)}
                                className="text-red-600 hover:text-red-900 text-xs px-2 py-1 rounded bg-red-50 hover:bg-red-100"
                              >
                                Delete
                              </button>
                            </div>
                            <button
                              onClick={() => togglePublishPeriod(period)}
                              className={`w-full text-xs px-2 py-1.5 rounded font-medium transition-colors ${
                                period.published_to_parents
                                  ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                  : 'bg-green-100 text-green-800 hover:bg-green-200'
                              }`}
                            >
                              {period.published_to_parents ? '🔒 Unpublish from Parents' : '👁 Publish to Parents'}
                            </button>
                            <select
                              value={period.is_active}
                              onChange={(e) => updatePeriodStatus(period.id, e.target.value)}
                              className="text-xs border border-gray-300 rounded px-2 py-1 w-full"
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                              <option value="closed">Closed</option>
                            </select>
                            {/* Show club count on mobile */}
                            <div className="sm:hidden text-xs text-gray-600">
                              {clubs[period.id]?.length || 0} clubs
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manage Clubs Tab */}
      {activeTab === 'clubs' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Manage Clubs</h2>
              {selectedPeriod && (
                <p className="text-sm text-gray-600">For: {selectedPeriod.name}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              <select
                value={selectedPeriod?.id || ''}
                onChange={(e) => {
                  const period = bookingPeriods.find(p => p.id == e.target.value);
                  setSelectedPeriod(period);
                }}
                className="border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Select Booking Period</option>
                {bookingPeriods.map((period) => (
                  <option key={period.id} value={period.id}>{period.name}</option>
                ))}
              </select>
              <button
                onClick={() => setShowBulkUpload(!showBulkUpload)}
                disabled={!selectedPeriod}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <span>📤</span>
                <span>Bulk Upload</span>
              </button>
              <button
                onClick={() => {
                  setShowClubForm(!showClubForm);
                  if (showClubForm) {
                    setEditingClub(null);
                    setClubForm({
                      name: '',
                      description: '',
                      day_of_week: 'Monday',
                      start_time: '15:30',
                      end_time: '16:30',
                      capacity: 20,
                      teacher_email: '',
                      location: '',
                      age_groups: [],
                      selected_students: [],
                      requirements: ''
                    });
                  }
                }}
                disabled={!selectedPeriod}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {showClubForm ? 'Cancel' : 'Add Club'}
              </button>
            </div>
          </div>

          {/* Bulk Upload Section */}
          {showBulkUpload && selectedPeriod && (
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">📤</span>
                <h3 className="text-lg font-semibold text-gray-900">Bulk Upload Clubs</h3>
              </div>
              
              <div className="space-y-4">
                {/* Instructions */}
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-2">📋 Instructions:</h4>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
                    <li>Download the Excel/CSV template below</li>
                    <li>Fill in your club information following the template format</li>
                    <li>Upload the completed file to create multiple clubs at once</li>
                  </ol>
                  
                  <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
                    <p className="text-sm text-blue-800">
                      <strong>Required columns:</strong> Club Name, Day of Week, Start Time, End Time, Capacity, Teacher Email
                    </p>
                    <p className="text-sm text-blue-800 mt-1">
                      <strong>Optional columns:</strong> Description, Teacher Name, Location, Requirements, Age Groups
                    </p>
                  </div>
                </div>

                {/* Download Template */}
                <div className="flex gap-3">
                  <button
                    onClick={downloadTemplate}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <span>📥</span>
                    <span>Download Template</span>
                  </button>
                </div>

                {/* File Upload */}
                <div className="bg-white rounded-lg p-4 border-2 border-dashed border-gray-300">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Filled Template (CSV or Excel)
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={(e) => {
                        setUploadFile(e.target.files[0]);
                        setUploadResult(null);
                      }}
                      className="flex-1 text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                    />
                    <button
                      onClick={handleBulkUpload}
                      disabled={!uploadFile || loading}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Uploading...' : 'Upload'}
                    </button>
                  </div>
                  {uploadFile && (
                    <p className="mt-2 text-sm text-gray-600">
                      Selected file: <span className="font-medium">{uploadFile.name}</span>
                    </p>
                  )}
                </div>

                {/* Upload Results */}
                {uploadResult && (
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-3">📊 Upload Results:</h4>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">{uploadResult.total_processed}</div>
                        <div className="text-xs text-blue-700">Total Rows</div>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">{uploadResult.total_created}</div>
                        <div className="text-xs text-green-700">Created</div>
                      </div>
                      <div className="text-center p-3 bg-red-50 rounded-lg">
                        <div className="text-2xl font-bold text-red-600">{uploadResult.total_errors}</div>
                        <div className="text-xs text-red-700">Errors</div>
                      </div>
                    </div>

                    {uploadResult.created_clubs && uploadResult.created_clubs.length > 0 && (
                      <div className="mb-4">
                        <h5 className="text-sm font-medium text-green-700 mb-2">✅ Successfully Created:</h5>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {uploadResult.created_clubs.map((club, index) => (
                            <div key={index} className="text-sm text-gray-700 bg-green-50 p-2 rounded">
                              {club.name} - {club.day} ({club.teacher})
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {uploadResult.errors && uploadResult.errors.length > 0 && (
                      <div>
                        <h5 className="text-sm font-medium text-red-700 mb-2">❌ Errors:</h5>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {uploadResult.errors.map((error, index) => (
                            <div key={index} className="text-sm text-red-700 bg-red-50 p-2 rounded">
                              {error}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Create Club Form */}
          {showClubForm && selectedPeriod && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingClub ? 'Edit Club' : 'Add New Club'}
              </h3>
              <form onSubmit={editingClub ? updateClub : createClub} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Club Name</label>
                    <input
                      type="text"
                      required
                      value={clubForm.name}
                      onChange={(e) => setClubForm({...clubForm, name: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="e.g., Football Club"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Teacher</label>
                    <select
                      required
                      value={clubForm.teacher_email}
                      onChange={(e) => setClubForm({...clubForm, teacher_email: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Select Teacher</option>
                      {staff.map((teacher) => (
                        <option key={teacher.email} value={teacher.email}>
                          {teacher.name} ({teacher.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Day</label>
                    <select
                      value={clubForm.day_of_week}
                      onChange={(e) => setClubForm({...clubForm, day_of_week: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {daysOfWeek.map((day) => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Capacity</label>
                    <input
                      type="number"
                      required
                      min="1"
                      max="100"
                      value={clubForm.capacity}
                      onChange={(e) => setClubForm({...clubForm, capacity: parseInt(e.target.value)})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                    <input
                      type="time"
                      required
                      value={clubForm.start_time}
                      onChange={(e) => setClubForm({...clubForm, start_time: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                    <input
                      type="time"
                      required
                      value={clubForm.end_time}
                      onChange={(e) => setClubForm({...clubForm, end_time: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <input
                      type="text"
                      value={clubForm.location}
                      onChange={(e) => setClubForm({...clubForm, location: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="e.g., Sports Hall, Art Room"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={clubForm.description}
                      onChange={(e) => setClubForm({...clubForm, description: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows="3"
                      placeholder="Describe the club activities..."
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Eligible Students (Hierarchical Selection)
                    </label>
                    <div className="border border-gray-300 rounded-lg p-4 max-h-96 overflow-y-auto bg-gray-50">
                      <div className="mb-2 text-sm text-gray-600">
                        Selected: <span className="font-bold text-purple-600">{clubForm.selected_students?.length || 0}</span> student(s)
                      </div>

                      {allStudents.length === 0 ? (
                        <div className="text-center py-4">
                          <p className="text-gray-500 text-sm">Loading students...</p>
                        </div>
                      ) : (
                        getYearGroups().map(year => (
                          <div key={year} className="mb-2 border border-gray-200 rounded-lg">
                            {/* Year Header */}
                            <button
                              type="button"
                              onClick={() => toggleYear(year)}
                              className="w-full px-3 py-2 flex items-center justify-between bg-gray-100 hover:bg-gray-200 rounded-t-lg"
                            >
                              <span className="font-semibold text-gray-900 flex items-center gap-2 text-sm">
                                <span>{expandedYears.includes(year) ? '▼' : '▶'}</span>
                                {year}
                              </span>
                              <span className="text-xs text-gray-600">
                                {getFormsInYear(year).length} form(s)
                              </span>
                            </button>

                            {/* Forms in Year */}
                            {expandedYears.includes(year) && (
                              <div className="border-t border-gray-200">
                                {getFormsInYear(year).map(form => {
                                  const formKey = `${year}-${form}`;
                                  const studentsInForm = getStudentsInForm(year, form);
                                  const allSelected = studentsInForm.every(s => isStudentSelected(s.school_id));
                                  
                                  return (
                                    <div key={formKey} className="border-b border-gray-100 last:border-b-0">
                                      {/* Form Header */}
                                      <div className="flex items-center bg-white hover:bg-gray-50">
                                        <button
                                          type="button"
                                          onClick={() => toggleForm(formKey)}
                                          className="flex-1 px-4 py-1.5 flex items-center justify-between text-left"
                                        >
                                          <span className="font-medium text-gray-800 flex items-center gap-2 text-sm">
                                            <span className="text-xs">{expandedForms.includes(formKey) ? '▼' : '▶'}</span>
                                            Form {form}
                                          </span>
                                          <span className="text-xs text-gray-600">
                                            {studentsInForm.length} student(s)
                                          </span>
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => selectAllInForm(year, form)}
                                          className={`px-2 py-1 text-xs font-medium ${
                                            allSelected 
                                              ? 'text-purple-700 bg-purple-100 hover:bg-purple-200' 
                                              : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                                          } rounded mr-2`}
                                        >
                                          {allSelected ? '✓ All' : 'Select All'}
                                        </button>
                                      </div>

                                      {/* Students in Form */}
                                      {expandedForms.includes(formKey) && (
                                        <div className="bg-gray-50 px-6 py-1">
                                          {studentsInForm.map(student => (
                                            <label
                                              key={student.school_id}
                                              className="flex items-center py-1.5 hover:bg-white px-2 rounded cursor-pointer"
                                            >
                                              <input
                                                type="checkbox"
                                                checked={isStudentSelected(student.school_id)}
                                                onChange={() => toggleStudent(student.school_id)}
                                                className="mr-2 h-3 w-3 text-purple-600 rounded"
                                              />
                                              <span className="text-xs text-gray-800">
                                                {student.full_name} 
                                                <span className="text-gray-500 ml-1">({student.school_id})</span>
                                              </span>
                                            </label>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Requirements/Notes</label>
                    <textarea
                      value={clubForm.requirements}
                      onChange={(e) => setClubForm({...clubForm, requirements: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows="2"
                      placeholder="Any special requirements or notes..."
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowClubForm(false);
                      setEditingClub(null);
                      setClubForm({
                        name: '',
                        description: '',
                        day_of_week: 'Monday',
                        start_time: '15:30',
                        end_time: '16:30',
                        capacity: 20,
                        teacher_email: '',
                        location: '',
                        age_groups: [],
                        selected_students: [],
                        requirements: ''
                      });
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    {loading ? (editingClub ? 'Updating...' : 'Creating...') : (editingClub ? 'Update Club' : 'Create Club')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Clubs List */}
          {selectedPeriod && (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {!clubs[selectedPeriod.id] || clubs[selectedPeriod.id].length === 0 ? (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">🏃‍♂️</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Clubs Yet</h3>
                  <p className="text-gray-600">Add your first club to this booking period</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
                  {clubs[selectedPeriod.id].map((club) => (
                    <div key={club.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-semibold text-gray-900">{club.name}</h4>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            club.is_full ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {club.current_bookings}/{club.capacity}
                          </span>
                          <button
                            onClick={() => editClub(club)}
                            className="text-blue-600 hover:text-blue-900 text-xs px-2 py-1 border border-blue-300 rounded"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteClub(club)}
                            className="text-red-600 hover:text-red-900 text-xs px-2 py-1 border border-red-300 rounded"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <div>📅 {club.day_of_week}</div>
                        <div>🕐 {club.start_time} - {club.end_time}</div>
                        <div>👨‍🏫 {club.teacher_name}</div>
                        {club.location && <div>📍 {club.location}</div>}
                        {club.selected_students && club.selected_students.length > 0 ? (
                          <div>🎯 {getFormsFromSelectedStudents(club.selected_students).join(', ')} ({club.selected_students.length} students)</div>
                        ) : (
                          <div className="text-gray-400">🎯 No students assigned</div>
                        )}
                      </div>
                      {club.description && (
                        <p className="text-sm text-gray-600 mt-2">{club.description}</p>
                      )}
                      {club.requirements && (
                        <p className="text-xs text-orange-600 mt-2 bg-orange-50 p-2 rounded">
                          📝 {club.requirements}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* View Bookings Tab */}
      {activeTab === 'bookings' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h2 className="text-xl font-semibold text-gray-900">Club Bookings Overview</h2>
            
            {/* Period Filter */}
            <div className="w-full sm:w-auto">
              <select
                value={selectedBookingPeriod?.id || ''}
                onChange={(e) => {
                  const period = bookingPeriods.find(p => p.id === parseInt(e.target.value));
                  setSelectedBookingPeriod(period || null);
                }}
                className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">All Periods</option>
                {bookingPeriods.map(period => (
                  <option key={period.id} value={period.id}>
                    {period.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Booking Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{filteredBookings.length}</div>
              <div className="text-blue-600 text-sm">Total Bookings</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {filteredBookings.filter(b => b.booking_status === 'confirmed').length}
              </div>
              <div className="text-green-600 text-sm">Confirmed</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {filteredBookings.filter(b => b.booking_status === 'waitlist').length}
              </div>
              <div className="text-yellow-600 text-sm">Waitlist</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                {filteredBookings.filter(b => b.booking_status === 'cancelled').length}
              </div>
              <div className="text-red-600 text-sm">Cancelled</div>
            </div>
          </div>

          {/* Available Clubs Summary */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Available Clubs {selectedBookingPeriod ? `- ${selectedBookingPeriod.name}` : ''}
              </h3>
            </div>
            
            {selectedBookingPeriod && clubs[selectedBookingPeriod.id] ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Club Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Schedule
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Teacher
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Capacity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bookings
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {clubs[selectedBookingPeriod.id].map((club) => {
                      const clubBookings = filteredBookings.filter(b => b.club_id === club.id);
                      const confirmedCount = clubBookings.filter(b => b.booking_status === 'confirmed').length;
                      const waitlistCount = clubBookings.filter(b => b.booking_status === 'waitlist').length;
                      const isNearFull = confirmedCount >= club.capacity * 0.8;
                      const isFull = confirmedCount >= club.capacity;
                      
                      return (
                        <tr key={club.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{club.name}</div>
                              {club.description && (
                                <div className="text-sm text-gray-500">{club.description}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div>{club.day_of_week}</div>
                            <div className="text-xs text-gray-500">
                              {club.start_time} - {club.end_time}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div>{club.teacher_name || 'Not assigned'}</div>
                            <div className="text-xs text-gray-500">{club.teacher_email}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {confirmedCount} / {club.capacity}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div className="space-y-1">
                              <div className="text-green-600">✓ {confirmedCount} confirmed</div>
                              {waitlistCount > 0 && (
                                <div className="text-yellow-600">⏳ {waitlistCount} waitlist</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              isFull 
                                ? 'bg-red-100 text-red-800' 
                                : isNearFull 
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {isFull ? 'Full' : isNearFull ? 'Nearly Full' : 'Available'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                {selectedBookingPeriod ? 'No clubs available for this period.' : 'Select a booking period to view available clubs.'}
              </div>
            )}
          </div>

          {/* Confirmed Bookings List */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Confirmed Bookings ({filteredBookings.filter(b => b.booking_status === 'confirmed').length})
              </h3>
            </div>
            
            {filteredBookings.filter(b => b.booking_status === 'confirmed').length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Student
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Club
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Parent
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Booking Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredBookings
                      .filter(booking => booking.booking_status === 'confirmed')
                      .sort((a, b) => new Date(b.booked_at) - new Date(a.booked_at))
                      .map((booking) => (
                        <tr key={booking.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{booking.student_name}</div>
                              <div className="text-sm text-gray-500">ID: {booking.student_school_id}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{booking.club_name}</div>
                              <div className="text-sm text-gray-500">
                                {booking.day_of_week} • {booking.start_time} - {booking.end_time}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{booking.parent_name}</div>
                              <div className="text-sm text-gray-500">{booking.parent_email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {new Date(booking.booked_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              booking.booking_status === 'confirmed' 
                                ? 'bg-green-100 text-green-800'
                                : booking.booking_status === 'waitlist'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {booking.booking_status}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                No confirmed bookings found.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Attendance Tab */}
      {activeTab === 'attendance' && (
        <div className="space-y-6">
          {/* Import and use ClubsAttendanceReport component content here */}
          <ClubsAttendanceReportInline />
        </div>
      )}
      </div>
    </div>
  );
};

export default ClubBookingManager;
